import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiUrl } from '../config/api';
import { resolveAccess } from '../utils/access';

export default function EmployeeCard(){
  const navigate = useNavigate();
  const params = useParams();
  const isNew = !params.id;
  const employeeId = params.id ? encodeURIComponent(params.id) : '';
  const [data, setData] = React.useState(() => ({
    user_id:'',
    last_name:'',
    first_name:'',
    middle_name:'',
    phone:'',
    email:'',
    citizenship:'',
    registration:'',
    registration_to:'',
    work_object:'',
    work_address:'',
    position:'',
    contract_number:'',
    contract_date:'',
    contract_from:'',
    contract_to:'',
    _transfer_date:'',
    birth_date:'',
    passport_series:'',
    passport_number:'',
    passport_issue_date:'',
    passport_issued_by:'',
    has_residence_permit:false,
    residence_permit_series:'',
    residence_permit_number:'',
    residence_permit_issue_date:'',
    residence_permit_issued_by:'',
    patent_series:'',
    patent_number:'',
    patent_region:'',
    patent_form_series:'',
    patent_form_number:'',
    patent_issue_date:'',
    photo_url:null,
    photo_updated_at:null,
    comments:[],
  }));
  const [loading, setLoading] = React.useState(!isNew);
  const [positions, setPositions] = React.useState([]);
  const [positionId, setPositionId] = React.useState('');
  const [transferPositionId, setTransferPositionId] = React.useState('');
  const [objects, setObjects] = React.useState([]);
  const [objectSearch, setObjectSearch] = React.useState('');
  const [objectDropdownOpen, setObjectDropdownOpen] = React.useState(false);
  const [objectSelectionError, setObjectSelectionError] = React.useState('');
  const objectFieldRef = React.useRef(null);
  const [documents, setDocuments] = React.useState([]);
  const [archivedDocuments, setArchivedDocuments] = React.useState([]);
  const [documentsLoading, setDocumentsLoading] = React.useState(false);
  const [documentsArchiveLoading, setDocumentsArchiveLoading] = React.useState(false);
  const [documentsModalOpen, setDocumentsModalOpen] = React.useState(false);
  const [documentsModalMode, setDocumentsModalMode] = React.useState('active');
  const [documentsUploading, setDocumentsUploading] = React.useState(false);
  const [selectedDocIds, setSelectedDocIds] = React.useState([]);
  const masterDocSelectRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const photoInputRef = React.useRef(null);
  const [photoUploading, setPhotoUploading] = React.useState(false);
  const [photoDeleting, setPhotoDeleting] = React.useState(false);
  const [contracts, setContracts] = React.useState([]);
  const [contractGenerating, setContractGenerating] = React.useState(null);
  const [showAssignModal, setShowAssignModal] = React.useState(false);
  const [assignState, setAssignState] = React.useState({ contract_type:'gph', contract_date:'', contract_from:'', contract_to:'' });
  const [editContractModal, setEditContractModal] = React.useState(null);
  const [posHistory, setPosHistory] = React.useState([]);
  const [tasks, setTasks] = React.useState([]);
  const [commentDraft, setCommentDraft] = React.useState('');
  const [commentSaving, setCommentSaving] = React.useState(false);
  const [completeTaskModal, setCompleteTaskModal] = React.useState(null);
  const [autoSaveUnlocked, setAutoSaveUnlocked] = React.useState(!isNew);
  const [autoSaveState, setAutoSaveState] = React.useState(() => (
    isNew
      ? { status:'blocked', message:'Нажмите «Создать сотрудника», чтобы выполнить первое сохранение' }
      : { status:'saved', message:'Все изменения сохранены' }
  ));
  const [duplicateModalOpen, setDuplicateModalOpen] = React.useState(false);
  const [duplicateMatches, setDuplicateMatches] = React.useState([]);
  const [duplicateCheckBusy, setDuplicateCheckBusy] = React.useState(false);

  const token = React.useMemo(() => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem('authToken') || '';
  }, []);
  const authHeaders = React.useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);
  const jsonHeaders = React.useMemo(() => ({ ...authHeaders, 'Content-Type': 'application/json' }), [authHeaders]);
  const authUser = React.useMemo(() => {
    if (typeof window === 'undefined') return null;
    try { return JSON.parse(localStorage.getItem('authUser') || 'null'); }
    catch { return null; }
  }, []);
  const access = React.useMemo(() => resolveAccess(authUser || {}), [authUser]);
  const { isSuper, isAdmin, hrOnly, hasHrRole: isHr } = access;
  const allowManualContractFields = isSuper;

  const autoSaveTimerRef = React.useRef(null);
  const pendingPayloadRef = React.useRef(null);
  const pendingComparableRef = React.useRef(null);
  const pendingModeRef = React.useRef(isNew ? 'create' : 'update');
  const activeComparableRef = React.useRef(null);
  const saveInFlightRef = React.useRef(false);
  const lastSavedSnapshotRef = React.useRef(null);
  const duplicateCheckTimerRef = React.useRef(null);
  const duplicateCheckSeqRef = React.useRef(0);

  React.useEffect(() => {
    pendingModeRef.current = isNew ? 'create' : 'update';
  }, [isNew]);

  const setDataFromServer = React.useCallback((producer) => {
    setData(prev => {
      const next = typeof producer === 'function' ? producer(prev) : producer;
      lastSavedSnapshotRef.current = JSON.stringify(buildComparableState(next, isNew ? positionId : null));
      return next;
    });
  }, [isNew, positionId, setData]);

  React.useEffect(()=>{
    if (!isNew) {
      setLoading(true);
      fetch(apiUrl(`/api/employees/${encodeURIComponent(params.id || '')}`), { headers: token? { Authorization: `Bearer ${token}` } : {} })
        .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
        .then(({ok,data})=> {
          if (ok) setDataFromServer(prev => mergeEmployeeState(prev, data)); else alert('Не удалось загрузить сотрудника');
        })
        .catch(()=> alert('Сбой сети'))
        .finally(()=> setLoading(false));
    }
  }, [isNew, params.id, token, setDataFromServer]);

  React.useEffect(()=>{
    fetch(apiUrl('/api/positions'), { headers: token? { Authorization: `Bearer ${token}` } : {} })
      .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
      .then(({ok, data:rows})=> {
        if (ok && Array.isArray(rows)) {
          let filtered = rows;
          if (!isSuper) filtered = filtered.filter(p=> p.name!=='Генеральный директор');
          if (hrOnly) filtered = filtered.filter(p=> !['gd','admin'].includes(String(p.default_role||'')));
          setPositions(filtered);
          if (!isNew && filtered.length){
            const found = filtered.find(p=> String(p.name||'').trim() === String(data.position||'').trim());
            if (found) setPositionId(String(found.id));
          }
        }
      })
      .catch(()=>{})
  }, [token, isNew, data.position, isSuper, hrOnly]);

  React.useEffect(()=>{
    fetch(apiUrl('/api/objects'), { headers: token? { Authorization: `Bearer ${token}` } : {} })
      .then(r=> r.json().then(d=> ({ ok:r.ok, data:d })))
      .then(({ok, data:rows})=> {
        if (ok && Array.isArray(rows)){
          const sorted = [...rows].sort((a,b)=> String(a.name||'').localeCompare(String(b.name||''), 'ru'));
          setObjects(sorted);
        } else {
          setObjects([]);
        }
      })
      .catch(()=> setObjects([]));
  }, [token]);

  React.useEffect(()=>{
    function onClickOutside(e){
      if (!objectFieldRef.current) return;
      if (!objectFieldRef.current.contains(e.target)) setObjectDropdownOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return ()=> document.removeEventListener('mousedown', onClickOutside);
  }, []);

  React.useEffect(()=>{
    return ()=> {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (duplicateCheckTimerRef.current) clearTimeout(duplicateCheckTimerRef.current);
    };
  }, []);

  const checkEmployeeDuplicates = React.useCallback(async (lastName, firstName, options = {}) => {
    const last = String(lastName || '').trim();
    const first = String(firstName || '').trim();
    if (!last || !first) return [];
    const params = new URLSearchParams({ last_name: last, first_name: first });
    const resp = await fetch(apiUrl(`/api/employees/duplicates/search?${params.toString()}`), { headers: authHeaders });
    const payload = await resp.json().catch(() => ({}));
    if (!resp.ok){
      throw new Error(payload?.message || payload?.error || `status_${resp.status}`);
    }
    const matches = Array.isArray(payload?.matches) ? payload.matches : [];
    if (matches.length && options.openModal !== false){
      setDuplicateMatches(matches);
      setDuplicateModalOpen(true);
    }
    return matches;
  }, [authHeaders]);

  React.useEffect(() => {
    if (!isNew) return;
    const last = (data.last_name || '').trim();
    const first = (data.first_name || '').trim();
    if (!last || !first){
      if (duplicateCheckTimerRef.current){
        clearTimeout(duplicateCheckTimerRef.current);
        duplicateCheckTimerRef.current = null;
      }
      setDuplicateCheckBusy(false);
      setDuplicateMatches([]);
      setDuplicateModalOpen(false);
      return;
    }

    if (duplicateCheckTimerRef.current) clearTimeout(duplicateCheckTimerRef.current);
    duplicateCheckTimerRef.current = setTimeout(async () => {
      const requestId = duplicateCheckSeqRef.current + 1;
      duplicateCheckSeqRef.current = requestId;
      setDuplicateCheckBusy(true);
      try {
        const matches = await checkEmployeeDuplicates(last, first, { openModal:false });
        if (duplicateCheckSeqRef.current !== requestId) return;
        if (matches.length){
          setDuplicateMatches(matches);
          setDuplicateModalOpen(true);
          setAutoSaveState({ status:'blocked', message:'Найден возможный дубль сотрудника. Откройте существующую карточку.' });
        } else {
          setDuplicateMatches([]);
          setDuplicateModalOpen(false);
        }
      } catch (error) {
        if (duplicateCheckSeqRef.current !== requestId) return;
        console.error('DUPLICATE_CHECK_ERROR', error);
      } finally {
        if (duplicateCheckSeqRef.current === requestId) setDuplicateCheckBusy(false);
      }
    }, 500);

    return () => {
      if (duplicateCheckTimerRef.current){
        clearTimeout(duplicateCheckTimerRef.current);
        duplicateCheckTimerRef.current = null;
      }
    };
  }, [isNew, data.last_name, data.first_name, checkEmployeeDuplicates]);

  React.useEffect(()=>{
    if (objectDropdownOpen) return;
    if (!data.work_object){
      setObjectSearch('');
      return;
    }
    const matched = objects.find(obj => (obj.name||'') === data.work_object);
    setObjectSearch(matched ? formatObjectLabel(matched) : data.work_object);
  }, [data.work_object, objects, objectDropdownOpen]);

  const filteredObjects = React.useMemo(()=>{
    const q = objectSearch.trim().toLowerCase();
    if (!q) return objects.slice(0, 20);
    return objects.filter(obj => {
      const name = String(obj.name||'').toLowerCase();
      const codeBase = String(obj.codeBase||obj.code||'').toLowerCase();
      return name.includes(q) || codeBase.includes(q);
    }).slice(0, 20);
  }, [objectSearch, objects]);

  React.useEffect(()=>{
    if (!data.work_object || data.work_address) return;
    const matched = objects.find(obj => (obj.name||'') === data.work_object);
    if (matched?.address){
      setData(s => ({ ...s, work_address: matched.address }));
    }
  }, [objects, data.work_object, data.work_address]);

  function handleObjectInputChange(value){
    setObjectSearch(value);
    setObjectDropdownOpen(true);
  }

  function handleObjectSelect(obj){
    if (!obj){
      onChange('work_object', '');
      onChange('work_address', '');
      setObjectSearch('');
      setObjectDropdownOpen(false);
      setObjectSelectionError('');
      return;
    }
    onChange('work_object', obj.name || formatObjectLabel(obj));
    if (obj.address) onChange('work_address', obj.address);
    setObjectSearch(formatObjectLabel(obj));
    setObjectDropdownOpen(false);
    setObjectSelectionError('');
  }

  const ensureObjectSelected = React.useCallback((payload, options = {}) => {
    const silent = options?.silent === true;
    const searchValue = objectSearch.trim();
    const currentValue = payload.work_object ? String(payload.work_object).trim() : '';
    if (!searchValue && !currentValue){
      setObjectSelectionError('');
      return payload;
    }

    const storedObj = objects.find(obj => String(obj.name||'').trim().toLowerCase() === currentValue.toLowerCase());
    const storedLabel = storedObj ? formatObjectLabel(storedObj) : currentValue;
    const needResolve = searchValue && (!currentValue || searchValue.toLowerCase() !== storedLabel.trim().toLowerCase());
    if (!needResolve){
      setObjectSelectionError('');
      return payload;
    }

    const matched = findObjectMatch(searchValue, objects);
    if (!matched){
      if (silent) setObjectSelectionError('Выберите объект из списка');
      else alert('Выберите объект из списка');
      setObjectDropdownOpen(true);
      return null;
    }
    const resolvedName = matched.name || formatObjectLabel(matched);
    const resolvedAddress = payload.work_address || matched.address || '';
    payload.work_object = resolvedName;
    payload.work_address = resolvedAddress;
    setData(s => ({ ...s, work_object: resolvedName, work_address: s.work_address || matched.address || '' }));
    setObjectSearch(formatObjectLabel(matched));
    setObjectSelectionError('');
    return payload;
  }, [objectSearch, objects, setData]);

  function openDocumentsModal(mode = 'active') {
    setDocumentsModalMode(mode);
    fetchDocuments(mode);
    setDocumentsModalOpen(true);
  }

  function openFileDialog(){
    if (isNew || !params.id){
      alert('Сначала создайте карточку сотрудника, чтобы прикреплять файлы.');
      return;
    }
    if (fileInputRef.current){
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }

  function handleFilesSelected(e) {
    const input = e.target;
    const files = Array.from(input?.files || []);
    if (!files.length) {
      if (input) input.value = '';
      return;
    }
    if (isNew || !params.id) {
      alert('Сначала сохраните карточку сотрудника.');
      if (input) input.value = '';
      return;
    }

    const allowedFormats = ['image/jpeg', 'image/png', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const invalidFiles = files.filter(file => !allowedFormats.includes(file.type));

    if (invalidFiles.length) {
      alert(`Некоторые файлы имеют недопустимый формат: ${invalidFiles.map(f => f.name).join(', ')}`);
      if (input) input.value = '';
      return;
    }

    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    setDocumentsUploading(true);
    fetch(apiUrl(`/api/employees/${employeeId}/documents`), {
      method: 'POST',
      headers: authHeaders,
      body: formData
    })
      .then(r => r.json().then(d => ({ ok: r.ok, data: d, status: r.status })))
      .then(({ ok, data, status }) => {
        if (!ok) {
          const msg = data?.error || `status_${status}`;
          alert('Не удалось загрузить файлы: ' + msg);
          return;
        }
        if (Array.isArray(data?.documents)) setDocuments(data.documents);
        fetchDocuments('active');
        fetchDocuments('archived');
        setSelectedDocIds([]);
      })
      .catch(() => alert('Ошибка сети при загрузке файлов'))
      .finally(() => {
        setDocumentsUploading(false);
        if (input) input.value = '';
      });
  }

  function openPhotoDialog(){
    if (photoInputRef.current){
      photoInputRef.current.value = '';
      photoInputRef.current.click();
    }
  }

  React.useEffect(() => {
    setSelectedDocIds([]);
  }, [documentsModalOpen, documentsModalMode]);

  function handlePhotoSelected(e){
    const file = e.target.files?.[0];
    if (!file){
      return;
    }
    if (file.size > 5 * 1024 * 1024){
      alert('Фото больше 5 МБ');
      e.target.value='';
      return;
    }
    if (!params.id){
      alert('Дождитесь автоматического сохранения карточки перед загрузкой фото');
      e.target.value='';
      return;
    }
    const formData = new FormData();
    formData.append('photo', file);
    setPhotoUploading(true);
    fetch(apiUrl(`/api/employees/${employeeId}/photo`), {
      method:'POST',
      headers: authHeaders,
      body: formData
    })
      .then(r=> r.json().then(d=> ({ ok:r.ok, data:d, status:r.status })))
      .then(({ok,data,status})=> {
        if (!ok){
          const err = data?.error || `status_${status}`;
          if (err==='file_too_large') alert('Файл отклонен сервером: больше 5 МБ.');
          else alert('Не удалось загрузить фото: '+err);
          return;
        }
        if (data?.employee) setDataFromServer(prev => mergeEmployeeState(prev, data.employee));
      })
      .catch(()=> alert('Сбой сети при загрузке фото'))
      .finally(()=> {
        setPhotoUploading(false);
        if (e.target) e.target.value='';
      });
  }

  function removePhoto(){
    if (!params.id || !photoUrl) return;
    if (!window.confirm('Удалить фото сотрудника?')) return;
    setPhotoDeleting(true);
    fetch(apiUrl(`/api/employees/${employeeId}/photo`), {
      method:'DELETE',
      headers: authHeaders
    })
      .then(r=> r.json().then(d=> ({ ok:r.ok, data:d })))
      .then(({ok,data})=> {
        if (!ok){
          alert('Не удалось удалить фото');
          return;
        }
        if (data?.employee) setDataFromServer(prev => mergeEmployeeState(prev, data.employee));
      })
      .catch(()=> alert('Сбой сети при удалении фото'))
      .finally(()=> setPhotoDeleting(false));
  }

  function generateContractDoc(contractId){
    if (isNew || !params.id || !contractId) return;
    setContractGenerating(contractId);
    fetch(apiUrl(`/api/employees/${employeeId}/contracts/generate`), {
      method:'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ contract_id: contractId })
    })
      .then(r=> r.json().then(d=> ({ ok:r.ok, data:d, status:r.status })))
      .then(({ok,data,status})=> {
        if (!ok){
          const msg = data?.message || data?.error || `status_${status}`;
          alert(`Не удалось сформировать договор: ${msg}`);
          return;
        }
        const doc = data?.document;
        if (doc){
          setDocuments(prev => {
            const base = Array.isArray(prev) ? prev.filter(existing => existing.id !== doc.id) : [];
            return [doc, ...base];
          });
          fetchDocuments('active');
          setContracts(prev => prev.map(item => item.id === (data?.contract_id || contractId) ? { ...item, document: doc } : item));
        }
        alert('Договор сформирован и сохранен в карточке.');
      })
      .catch(()=> alert('Сбой сети при генерации договора'))
      .finally(()=> setContractGenerating(null));
  }

  function openContractDocument(doc){
    if (!doc) return;
    const url = getDocumentUrl(doc);
    if (url && url !== '#') window.open(url, '_blank', 'noopener');
  }

  function deleteDocument(docId){
    if (!docId || !params.id) return;
    if (!window.confirm('Удалить файл?')) return;
    fetch(apiUrl(`/api/employees/${employeeId}/documents/${docId}`), {
      method:'DELETE',
      headers: authHeaders
    })
      .then(r=> r.json().then(d=> ({ ok:r.ok, data:d })))
      .then(({ok})=> {
        if (!ok){ alert('Не удалось удалить файл'); return; }
        setSelectedDocIds((prev) => prev.filter((id) => id !== String(docId)));
        setContracts(prev => prev.map(contract => (contract.document?.id === docId ? { ...contract, document:null } : contract)));
        fetchDocuments('active');
        fetchDocuments('archived');
      })
      .catch(()=> alert('Сбой сети при удалении файла'));
  }

  function toggleDocumentArchive(doc, nextValue){
    if (!doc?.id || !params.id) return;
    fetch(apiUrl(`/api/employees/${employeeId}/documents/${doc.id}/archive`), {
      method:'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ archived: !!nextValue })
    })
      .then(r=> r.json().then(d=> ({ ok:r.ok, data:d })))
      .then(({ok})=> {
        if (!ok){ alert('Не удалось обновить архивный статус файла'); return; }
        fetchDocuments('active');
        fetchDocuments('archived');
      })
      .catch(()=> alert('Сбой сети при обновлении статуса файла'));
  }

  React.useEffect(()=>{
    if (!isNew){
      fetch(apiUrl(`/api/employees/${employeeId}/contracts`), { headers: authHeaders })
        .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
        .then(({ok,data})=> { if (ok && Array.isArray(data)) setContracts(data); })
        .catch(()=>{});
      fetch(apiUrl(`/api/employees/${employeeId}/position-history`), { headers: authHeaders })
        .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
        .then(({ok,data})=> { if (ok && Array.isArray(data)) setPosHistory(data); })
        .catch(()=>{});
      fetch(apiUrl(`/api/employees/${employeeId}/contract-tasks`), { headers: authHeaders })
        .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
        .then(({ok,data})=> { if (ok && Array.isArray(data)) setTasks(data); })
        .catch(()=>{});
    }
  }, [authHeaders, employeeId, isNew]);

  const fetchDocuments = React.useCallback((mode='active')=>{
    if (isNew || !params.id) return;
    const isArchived = mode === 'archived';
    const setItems = isArchived ? setArchivedDocuments : setDocuments;
    const setLoading = isArchived ? setDocumentsArchiveLoading : setDocumentsLoading;
    setLoading(true);
    const qs = isArchived ? '?archived=1' : '';
    fetch(apiUrl(`/api/employees/${employeeId}/documents${qs}`), { headers: authHeaders })
      .then(r=> r.json().then(d=> ({ ok:r.ok, data:d })))
      .then(({ok,data})=> {
        if (ok && Array.isArray(data)) setItems(data);
        else setItems([]);
      })
      .catch(()=> setItems([]))
        .finally(()=> setLoading(false));
      }, [authHeaders, employeeId, isNew, params.id]);

  React.useEffect(()=>{
    if (!isNew){
      fetchDocuments('active');
      fetchDocuments('archived');
    }
  }, [isNew, fetchDocuments]);

  function onChange(field, value){ setData(s=> ({ ...s, [field]: value })); }

  function updateNameField(field, value){
    setData(prev => {
      const next = { ...prev, [field]: value };
      const composed = composeFullName(
        (next.last_name || '').trim(),
        (next.first_name || '').trim(),
        (next.middle_name || '').trim()
      );
      next.full_name = composed;
      return next;
    });
  }

  const handleInitialCreate = React.useCallback(() => {
    if (autoSaveUnlocked) return;
    setAutoSaveUnlocked(true);
    setAutoSaveState(prev => (prev.status==='pending' ? prev : { status:'pending', message:'Готовим первое сохранение…' }));
  }, [autoSaveUnlocked]);

  const runAutoSave = React.useCallback(async () => {
    if (!pendingPayloadRef.current) return;
    if (autoSaveTimerRef.current){
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    if (saveInFlightRef.current){
      autoSaveTimerRef.current = setTimeout(runAutoSave, 600);
      return;
    }
    const payload = pendingPayloadRef.current;
    const mode = pendingModeRef.current;
    pendingPayloadRef.current = null;
    activeComparableRef.current = pendingComparableRef.current;
    pendingComparableRef.current = null;
    if (activeComparableRef.current) {
      lastSavedSnapshotRef.current = activeComparableRef.current;
    }
    saveInFlightRef.current = true;
    if (autoSaveTimerRef.current){
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    setAutoSaveState({ status:'saving', message: mode === 'create' ? 'Создаем карточку…' : 'Сохраняем изменения…' });
    try {
      if (mode === 'create'){
        const duplicateRows = await checkEmployeeDuplicates(payload.last_name, payload.first_name, { openModal:false });
        if (duplicateRows.length){
          setDuplicateMatches(duplicateRows);
          setDuplicateModalOpen(true);
          setAutoSaveState({ status:'blocked', message:'Сотрудник уже существует в базе. Перейдите в карточку и создайте новый договор.' });
          return;
        }
      }
      const url = mode === 'create' ? apiUrl('/api/employees') : apiUrl(`/api/employees/${employeeId}`);
      const method = mode === 'create' ? 'POST' : 'PATCH';
      const response = await fetch(url, { method, headers: jsonHeaders, body: JSON.stringify(payload) });
      const body = await response.json();
      if (!response.ok) throw new Error(body?.message || body?.error || 'server_error');
      if (mode === 'create'){
        if (body?.user_id) alert(`Сотрудник создан. ID / логин: ${body.user_id}. Пароль = логин.`);
        activeComparableRef.current = null;
        navigate(`/hr/employees/${body.id}`);
        return;
      }
      setDataFromServer(prev => mergeEmployeeState(prev, body));
      activeComparableRef.current = null;
      setAutoSaveState({ status:'saved', message:'Все изменения сохранены' });
    } catch (error) {
      console.error('AUTO_SAVE_ERROR', error);
      if (activeComparableRef.current) {
        lastSavedSnapshotRef.current = activeComparableRef.current;
      }
      activeComparableRef.current = null;
      setAutoSaveState({ status:'error', message: describeAutoSaveError(error) });
    } finally {
      saveInFlightRef.current = false;
    }
  }, [employeeId, jsonHeaders, navigate, setDataFromServer, checkEmployeeDuplicates]);

  React.useEffect(()=>{
    if (loading) return;
    const comparable = JSON.stringify(buildComparableState(data, isNew ? positionId : null));
    if (lastSavedSnapshotRef.current === null){
      lastSavedSnapshotRef.current = comparable;
      return;
    }
    if (!autoSaveUnlocked){
      if (autoSaveTimerRef.current){
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      pendingPayloadRef.current = null;
      pendingComparableRef.current = null;
      setAutoSaveState(prev => (
        prev.status==='blocked' && prev.message==='Нажмите «Создать сотрудника», чтобы выполнить первое сохранение'
          ? prev
          : { status:'blocked', message:'Нажмите «Создать сотрудника», чтобы выполнить первое сохранение' }
      ));
      return;
    }
    if (comparable === lastSavedSnapshotRef.current){
      if (autoSaveTimerRef.current){
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      pendingPayloadRef.current = null;
      pendingComparableRef.current = null;
      if (autoSaveState.status !== 'error'){
        setAutoSaveState(prev => (prev.status==='saved' ? prev : { status:'saved', message:'Все изменения сохранены' }));
      }
      return;
    }

    const trimmedLast = (data.last_name||'').trim();
    if (!trimmedLast){
      if (autoSaveTimerRef.current){
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      pendingPayloadRef.current = null;
      pendingComparableRef.current = null;
      setAutoSaveState(prev => (prev.status==='blocked' && prev.message==='Введите фамилию' ? prev : { status:'blocked', message:'Введите фамилию' }));
      return;
    }
    const trimmedFirst = (data.first_name||'').trim();
    if (!trimmedFirst){
      if (autoSaveTimerRef.current){
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      pendingPayloadRef.current = null;
      pendingComparableRef.current = null;
      setAutoSaveState(prev => (prev.status==='blocked' && prev.message==='Введите имя' ? prev : { status:'blocked', message:'Введите имя' }));
      return;
    }
    if (isNew && !positionId){
      if (autoSaveTimerRef.current){
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      pendingPayloadRef.current = null;
      pendingComparableRef.current = null;
      setAutoSaveState(prev => (prev.status==='blocked' && prev.message==='Выберите должность' ? prev : { status:'blocked', message:'Выберите должность' }));
      return;
    }
    const trimmedMiddle = (data.middle_name||'').trim();
    const payload = { ...data };
    payload.last_name = trimmedLast;
    payload.first_name = trimmedFirst;
    payload.middle_name = trimmedMiddle;
    payload.full_name = composeFullName(trimmedLast, trimmedFirst, trimmedMiddle) || `${trimmedLast} ${trimmedFirst}`.trim();
    delete payload.user_id;
    delete payload.contract_date;
    delete payload._transfer_date;
    delete payload.passport; delete payload.passport_full;
    if (isNew && positionId){ payload.position_id = Number(positionId); delete payload.position; }
    payload.registration_to = data.registration_to || '';
    const trimmedEmail = (payload.email || '').trim();
    payload.email = trimmedEmail ? trimmedEmail : null;
    payload.has_residence_permit = !!payload.has_residence_permit;
    if (!payload.has_residence_permit){
      payload.residence_permit_series = null;
      payload.residence_permit_number = null;
      payload.residence_permit_issue_date = null;
      payload.residence_permit_issued_by = null;
    }
    if (!allowManualContractFields) delete payload.contract_number;
    else {
      const trimmedContractNumber = (payload.contract_number || '').trim();
      payload.contract_number = trimmedContractNumber ? trimmedContractNumber : null;
    }
    const adjusted = ensureObjectSelected(payload, { silent:true });
    if (!adjusted){
      if (autoSaveTimerRef.current){
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      pendingPayloadRef.current = null;
      pendingComparableRef.current = null;
      const msg = objectSelectionError || 'Выберите объект из списка';
      setAutoSaveState(prev => (prev.status==='blocked' && prev.message===msg ? prev : { status:'blocked', message: msg }));
      return;
    }

    pendingPayloadRef.current = adjusted;
    pendingComparableRef.current = comparable;
    pendingModeRef.current = isNew ? 'create' : 'update';
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(runAutoSave, 1200);
    setAutoSaveState(prev => (prev.status==='pending' ? prev : { status:'pending', message:'Есть несохраненные изменения…' }));
  }, [data, isNew, positionId, loading, runAutoSave, objectSelectionError, autoSaveState.status, allowManualContractFields, autoSaveUnlocked, ensureObjectSelected]);

  function openAssign(){ setAssignState({ contract_type:'gph', contract_date:'', contract_from:'', contract_to:'' }); setShowAssignModal(true); }
  function closeAssign(){ setShowAssignModal(false); }
  function updateAssign(field, value){ setAssignState(s=> ({ ...s, [field]: value })); }
  function confirmAssign(){
    if (!assignState.contract_type || !assignState.contract_date){ alert('Укажите тип и дату договора'); return; }
    if (!assignState.contract_from) assignState.contract_from = assignState.contract_date; // если не задан период начала
    fetch(apiUrl(`/api/employees/${employeeId}/assign-contract`), {
      method:'POST', headers: jsonHeaders, body: JSON.stringify(assignState)
    })
      .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
      .then(({ok,data})=> {
        if (!ok){ alert('Ошибка: '+(data?.error||'server')); return; }
        setDataFromServer(prev => mergeEmployeeState(prev, data));
        // обновить историю
        fetch(apiUrl(`/api/employees/${employeeId}/contracts`), { headers: authHeaders })
          .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
          .then(({ok,data})=> { if (ok && Array.isArray(data)) setContracts(data); });
        fetch(apiUrl(`/api/employees/${employeeId}/contract-tasks`), { headers: authHeaders })
          .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
          .then(({ok,data})=> { if (ok && Array.isArray(data)) setTasks(data); });
        setShowAssignModal(false);
      })
      .catch(()=> alert('Сбой сети'))
  }

  function saveContractEdit(){
    if (!editContractModal?.id) return;
    if (!editContractModal.contract_to){ alert('Укажите дату окончания'); return; }
    fetch(apiUrl(`/api/employees/${employeeId}/contracts/${editContractModal.id}`), {
      method:'PATCH', headers: jsonHeaders, body: JSON.stringify({ contract_to: editContractModal.contract_to })
    })
      .then(r=> r.json().then(d=>({ ok:r.ok, data:d, status:r.status })))
      .then(({ok,data,status})=> {
        if (!ok){
          if (data?.error==='contract_overlap') alert('Ошибка: пересечение периодов. '+(data?.message||''));
          else alert('Ошибка сохранения договора: '+(data?.error||`status_${status}`));
          return;
        }
        // обновить список договоров
        fetch(apiUrl(`/api/employees/${employeeId}/contracts`), { headers: authHeaders })
          .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
          .then(({ok,data})=> { if (ok && Array.isArray(data)) setContracts(data); });
        // обновить основную карточку (даты текущего контракта)
        fetch(apiUrl(`/api/employees/${employeeId}`), { headers: authHeaders })
          .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
          .then(({ok,data})=> { if (ok) setDataFromServer(prev => mergeEmployeeState(prev, data)); });
        // обновить задачи (конец договора мог создать задачи end)
        fetch(apiUrl(`/api/employees/${employeeId}/contract-tasks`), { headers: authHeaders })
          .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
          .then(({ok,data})=> { if (ok && Array.isArray(data)) setTasks(data); });
        setEditContractModal(null);
      })
      .catch(()=> alert('Сбой сети'))
  }

  function deleteContract(id){
    if (!id) return;
    if (!window.confirm('Удалить договор? Действие необратимо.')) return;
    fetch(apiUrl(`/api/employees/${employeeId}/contracts/${id}`), {
      method:'DELETE', headers: jsonHeaders
    })
      .then(async r=> {
        let payload=null; try { payload = await r.json(); } catch(e){}
        return { ok:r.ok, status:r.status, data:payload };
      })
      .then(({ok,status,data})=> {
        if (!ok){
          alert('Ошибка удаления: '+(data?.error||`status_${status}`));
          return;
        }
        if (data?.employee) setDataFromServer(prev => mergeEmployeeState(prev, data.employee));
        if (Array.isArray(data?.contracts)) setContracts(data.contracts);
      })
      .catch(err=> {
        console.error('deleteContract fetch error', err);
        alert('Сбой сети при удалении (нет ответа от сервера)');
      });
  }

  function openCompleteTask(id) {
    setCompleteTaskModal({ id, date: (new Date()).toISOString().slice(0,10) });
  }
  function closeCompleteTask() {
    setCompleteTaskModal(null);
  }
  function confirmCompleteTask() {
    if (!completeTaskModal?.id || !completeTaskModal.date) return;
    fetch(apiUrl(`/api/employees/${employeeId}/contract-tasks/${completeTaskModal.id}/complete`), {
      method:'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ completed_at: completeTaskModal.date })
    })
      .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
      .then(({ok,data})=> {
        if (!ok){ alert('Ошибка выполнения задачи'); return; }
        setTasks(s=> s.map(t=> t.id===completeTaskModal.id ? { ...t, ...data } : t));
        setCompleteTaskModal(null);
      })
      .catch(()=> alert('Сбой сети'))
  }

  function formatTaskInfo(t){
    if (!t.completed_at) return '';
    try {
      const dt = new Date(t.completed_at);
      const dStr = dt.toLocaleDateString('ru-RU');
      const timeStr = dt.toLocaleTimeString('ru-RU',{ hour:'2-digit', minute:'2-digit' });
      return `${dStr} ${timeStr}${t.completed_by? ' · '+t.completed_by : ''}`;
    } catch { return t.completed_at + (t.completed_by? ' · '+t.completed_by : ''); }
  }

    function ensureEndTasks() {
      fetch(apiUrl(`/api/employees/${employeeId}/ensure-end-tasks`), { method:'POST', headers: jsonHeaders })
        .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
        .then(({ok,data})=> {
          if (!ok){ alert(data?.message || 'Ошибка создания задач'); return; }
          if (data.created === 0){ alert(data.message || 'Задачи уже существуют'); return; }
          fetch(apiUrl(`/api/employees/${employeeId}/contract-tasks`), { headers: authHeaders })
            .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
            .then(({ok,data})=> { if (ok && Array.isArray(data)) setTasks(data); });
        })
        .catch(()=> alert('Сбой сети'));
    }

  function formatBytes(value){
    if (value == null) return '—';
    const units = ['Б','КБ','МБ','ГБ'];
    let size = Number(value);
    if (isNaN(size) || size <= 0) return '0 Б';
    let idx = 0;
    while (size >= 1024 && idx < units.length - 1){
      size = size / 1024;
      idx += 1;
    }
    return `${size.toFixed(size >= 10 || idx === 0 ? 0 : 1)} ${units[idx]}`;
  }

  function formatDateTime(value){
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString('ru-RU');
    } catch { return value; }
  }

  function getDocumentUrl(doc, opts = {}){
    if (!doc?.url) return '#';
    const base = /^https?:\/\//i.test(doc.url) ? doc.url : apiUrl(doc.url);
    const wantsDownload = opts.download === true;
    const joiner = base.includes('?') ? '&' : '?';
    return wantsDownload ? `${base}${joiner}download=1` : base;
  }

  function isImageDoc(doc){
    const mime = (doc?.mime_type || doc?.mimeType || '').toLowerCase();
    if (mime.startsWith('image/')) return true;
    const name = String(doc?.name || doc?.original_name || doc?.filename || '').toLowerCase();
    return /\.(png|jpe?g|gif|webp|bmp|svg|jfif)$/.test(name);
  }

  function isPdfDoc(doc){
    const mime = (doc?.mime_type || doc?.mimeType || '').toLowerCase();
    if (mime === 'application/pdf' || mime === 'application/x-pdf') return true;
    const name = String(doc?.name || doc?.original_name || doc?.filename || '').toLowerCase();
    return /\.pdf$/.test(name);
  }

  function getEmployeePhotoUrl(value){
    if (!value) return null;
    if (/^https?:\/\//i.test(value)) return value;
    return apiUrl(value);
  }

  function decodeDocumentName(rawValue){
    const value = rawValue || '';
    if (!value) return '';
    const suspicious = /Ã|Ð|Ñ|Ò|Ó|Ô|Õ|Ö|×|Ø|Ù|Ú|Û|Ü|Ý|Þ|ß/.test(value);
    if (!suspicious) return value;
    const containsCyrillic = (text) => /[А-Яа-яЁё]/.test(text || '');
    const tryDecode = (decoderFn) => {
      try {
        const decoded = decoderFn();
        if (!decoded || decoded === value) return null;
        if (containsCyrillic(decoded) || containsCyrillic(value) === false) return decoded;
        return decoded;
      } catch {
        return null;
      }
    };
    if (typeof TextDecoder !== 'undefined') {
      const decoderAttempt = tryDecode(() => {
        const decoder = new TextDecoder('utf-8');
        const bytes = new Uint8Array(Array.from(value).map((ch) => ch.charCodeAt(0)));
        return decoder.decode(bytes);
      });
      if (decoderAttempt) return decoderAttempt;
    }
    if (typeof decodeURIComponent === 'function' && typeof escape === 'function') {
      const escapeAttempt = tryDecode(() => decodeURIComponent(escape(value)));
      if (escapeAttempt) return escapeAttempt;
    }
    return value;
  }

  const canComment = !isNew && (isSuper || isAdmin || isHr);
  const commentList = Array.isArray(data?.comments) ? data.comments : [];

  function saveComment(){
    if (!canComment) return;
    const text = commentDraft.trim();
    if (!text){ alert('Введите текст комментария'); return; }
    if (!params.id) return;
    setCommentSaving(true);
    fetch(apiUrl(`/api/employees/${employeeId}/comments`), {
      method:'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ text })
    })
      .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
      .then(({ok,data})=> {
        if (!ok){ alert('Не удалось сохранить комментарий'); return; }
        setData(s=> ({ ...s, comments: Array.isArray(data?.comments)? data.comments : [] }));
        setCommentDraft('');
      })
      .catch(()=> alert('Сбой сети при сохранении комментария'))
      .finally(()=> setCommentSaving(false));
  }

    function doTransfer(){
      if (!transferPositionId){ alert('Выберите должность для перевода'); return; }
      if (!data._transfer_date){ alert('Укажите дату перевода'); return; }
      fetch(apiUrl(`/api/employees/${employeeId}/transfer-position`), {
        method:'POST', headers: jsonHeaders, body: JSON.stringify({ position_id: Number(transferPositionId), change_date: data._transfer_date })
      })
        .then(async r=> { let parsed=null; try { parsed = await r.json(); } catch { parsed = null; } return { ok:r.ok, status:r.status, data: parsed }; })
        .then(({ok,data,status})=> {
          if (!ok){ alert('Ошибка перевода: '+(data?.error||`status_${status}`)); return; }
          setDataFromServer(prev => mergeEmployeeState(prev, data));
          setPositionId(''); setTransferPositionId(''); onChange('_transfer_date','');
          fetch(apiUrl(`/api/employees/${employeeId}/position-history`), { headers: authHeaders })
            .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
            .then(({ok,data})=> { if (ok && Array.isArray(data)) setPosHistory(data); });
        })
        .catch(()=> alert('Сбой сети'))
    }

  const isArchivedDocsView = documentsModalMode === 'archived';
  const visibleDocuments = isArchivedDocsView ? archivedDocuments : documents;
  const documentsBusy = isArchivedDocsView ? documentsArchiveLoading : documentsLoading;
  const photoUrl = getEmployeePhotoUrl(data.photo_url);
  const [previewDocIndex, setPreviewDocIndex] = React.useState(null);
  const [previewRotation, setPreviewRotation] = React.useState(0);
  const [previewScale, setPreviewScale] = React.useState(1);
  const [isPreviewFullscreen, setIsPreviewFullscreen] = React.useState(false);
  const previewModalRef = React.useRef(null);
  const currentPreviewDoc = previewDocIndex != null ? visibleDocuments[previewDocIndex] : null;
  React.useEffect(()=>{
    if (previewDocIndex != null && (previewDocIndex < 0 || previewDocIndex >= visibleDocuments.length)){
      setPreviewDocIndex(null);
    }
  }, [visibleDocuments, previewDocIndex]);
  React.useEffect(() => {
    setPreviewRotation(0);
    setPreviewScale(1);
  }, [currentPreviewDoc]);
  React.useEffect(() => {
    function onFullscreenChange(){
      const fullscreenEl = document.fullscreenElement;
      const insideModal = !!(previewModalRef.current && fullscreenEl && previewModalRef.current.contains(fullscreenEl));
      setIsPreviewFullscreen(insideModal);
    }
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return ()=> document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);
  const selectedDocuments = React.useMemo(
    () => visibleDocuments.filter((doc) => selectedDocIds.includes(String(doc.id))),
    [selectedDocIds, visibleDocuments]
  );
  const allVisibleSelected = visibleDocuments.length > 0 && selectedDocuments.length === visibleDocuments.length;
  const hasSelectedDocuments = selectedDocuments.length > 0;

  React.useEffect(() => {
    if (!documentsModalOpen) return;
    setSelectedDocIds((prev) => prev.filter((id) => visibleDocuments.some((doc) => String(doc.id) === id)));
  }, [documentsModalOpen, visibleDocuments]);

  React.useEffect(() => {
    if (masterDocSelectRef.current) {
      masterDocSelectRef.current.indeterminate = hasSelectedDocuments && !allVisibleSelected;
    }
  }, [allVisibleSelected, hasSelectedDocuments]);

  function handleDocumentSelect(docId, checked){
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(docId);
      else next.delete(docId);
      return Array.from(next);
    });
  }

  function handleSelectAllDocuments(checked){
    if (!checked){
      setSelectedDocIds([]);
      return;
    }
    setSelectedDocIds(visibleDocuments.map((doc) => String(doc.id)));
  }

  function clearDocumentSelection(){
    setSelectedDocIds([]);
  }

  function openDocumentPreview(e, doc, index){
    if (e){
      const isModifier = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1;
      if (isModifier) return; // позволяем открытие в новой вкладке как раньше
      e.preventDefault();
    }
    if (!doc) return;
    const idx = typeof index === 'number' ? index : visibleDocuments.findIndex((d)=> String(d.id) === String(doc.id));
    if (idx < 0) return;
    setPreviewDocIndex(idx);
  }

  function closeDocumentPreview(){
    setPreviewDocIndex(null);
  }

  function stepDocumentPreview(delta){
    setPreviewDocIndex((prev)=>{
      if (prev == null) return prev;
      const total = visibleDocuments.length;
      if (!total) return null;
      return (prev + delta + total) % total;
    });
  }

  function handlePreviewWheel(e){
    if (!isImageDoc(currentPreviewDoc)) return;
    if (!e.ctrlKey) return;
    e.preventDefault();
    e.stopPropagation();
    const direction = e.deltaY > 0 ? -0.1 : 0.1;
    setPreviewScale(prev => {
      const next = Math.min(5, Math.max(0.3, Number((prev + direction).toFixed(2))));
      return next;
    });
  }

  function togglePreviewFullscreen(){
    const target = previewModalRef.current;
    if (!target) return;
    if (document.fullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen().catch(()=>{});
      return;
    }
    if (target.requestFullscreen) target.requestFullscreen().catch(()=>{});
  }

  function downloadDocumentFile(doc){
    if (!doc) return;
    const url = getDocumentUrl(doc, { download: true });
    if (!url || url === '#') return;
    const safeName = decodeDocumentName(doc.name || doc.original_name || doc.filename || `document-${doc.id}`);
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener';
    link.download = safeName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function handleBulkDownload(){
    if (!selectedDocuments.length) return;
    selectedDocuments.forEach(downloadDocumentFile);
  }

  if (loading) {
    return <div style={{ padding:20 }}>Загрузка…</div>;
  }

  const employeeDisplayName = composeFullName(data.last_name, data.first_name, data.middle_name) || data.full_name || '';

  return (
    <div style={{ padding:'24px 20px', maxWidth: 1100, margin:'0 auto' }}>
      <h2 style={{ marginTop:0, fontWeight:800 }}>{isNew? 'Новый сотрудник' : `Сотрудник №${data.user_id}${employeeDisplayName ? ` ${employeeDisplayName}` : ''}`}</h2>
      {!isNew && (
        <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:18 }}>
          <button onClick={openFileDialog} disabled={documentsUploading} style={primaryBtn}>
            {documentsUploading? 'Загрузка...' : 'Добавить файл'}
          </button>
          <button onClick={()=> openDocumentsModal('active')} style={secondaryBtn}>
            Прикрепленные файлы ({documents.length})
          </button>
          <button onClick={()=> openDocumentsModal('archived')} style={secondaryBtn}>
            Архивные файлы ({archivedDocuments.length})
          </button>
          <input ref={fileInputRef} type='file' style={{ display:'none' }} multiple onChange={handleFilesSelected} />
        </div>
      )}
      {!isNew && (
        <div style={photoActionsWrapper}>
          <div style={photoPreviewBox}>
            {photoUrl ? (
              <img src={photoUrl} alt='Фото сотрудника' style={photoImg} />
            ) : (
              <div style={photoPlaceholder}>Нет фото</div>
            )}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            <button onClick={openPhotoDialog} disabled={photoUploading} style={primaryBtn}>
              {photoUploading? 'Загрузка фото…' : (photoUrl ? 'Заменить фото' : 'Загрузить фото')}
            </button>
            {photoUrl && (
              <button onClick={removePhoto} disabled={photoDeleting} style={{ ...secondaryBtn, color:'#b91c1c' }}>
                {photoDeleting? 'Удаление…' : 'Удалить фото'}
              </button>
            )}
            <div style={hint}>JPEG/PNG до 5 МБ. Показывается в карточке сотрудника.</div>
            {data.photo_updated_at && (
              <div style={{ fontSize:12, color:'#4b5563' }}>Обновлено: {formatDateTime(data.photo_updated_at)}</div>
            )}
            <input ref={photoInputRef} type='file' accept='image/*' style={{ display:'none' }} onChange={handlePhotoSelected} />
          </div>
        </div>
      )}

      {/* Личные данные */}
      <section style={cardStyle}>
        <h3 style={sectionTitle}>Личные данные</h3>
        <div style={grid4}>
          {!isNew && (
            <div>
              <label>ID (логин)</label>
              <input value={data.user_id||''} readOnly style={roInput} />
              <div style={hint}>Уникальный. Не изменяется.</div>
            </div>
          )}
          <div style={fioWrapper}>
            <div style={fioGrid}>
              <div>
                <label>Фамилия *</label>
                <input value={data.last_name||''} onChange={e=> updateNameField('last_name', e.target.value)} style={input} />
              </div>
              <div>
                <label>Имя *</label>
                <input value={data.first_name||''} onChange={e=> updateNameField('first_name', e.target.value)} style={input} />
              </div>
              <div>
                <label>Отчество (необязательно)</label>
                <input value={data.middle_name||''} onChange={e=> updateNameField('middle_name', e.target.value)} style={input} placeholder='—' />
              </div>
            </div>
            <div style={hint}>Отчество можно не указывать при его отсутствии.</div>
            {isNew && duplicateCheckBusy && (
              <div style={hint}>Проверяем дубль сотрудника по Фамилии и Имени…</div>
            )}
          </div>
          <div>
            <label>Телефон</label>
            <input value={data.phone||''} onChange={e=> onChange('phone', e.target.value)} style={input} />
          </div>
          <div>
            <label>E-mail</label>
            <input type='email' value={data.email||''} onChange={e=> onChange('email', e.target.value)} style={input} placeholder='name@example.com' />
          </div>
          <div>
            <label>Гражданство</label>
            <select value={data.citizenship||''} onChange={e=> onChange('citizenship', e.target.value)} style={input}>
              <option value=''>— выберите —</option>
              {citizenshipOptions.map(c=> <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label>Регистрация</label>
            <textarea rows={2} value={data.registration||''} onChange={e=> onChange('registration', e.target.value)} style={textareaInput} />
          </div>
          <div>
            <label>Срок регистрации до</label>
            <input type='date' value={data.registration_to||''} onChange={e=> onChange('registration_to', e.target.value)} style={input} />
          </div>
          <div ref={objectFieldRef} style={{ position:'relative' }}>
            <label>Объект (место работы)</label>
            <input
              value={objectSearch}
              onChange={e=> handleObjectInputChange(e.target.value)}
              onFocus={()=> setObjectDropdownOpen(true)}
              onKeyDown={e=> {
                if (e.key==='Enter'){
                  e.preventDefault();
                  if (filteredObjects.length) handleObjectSelect(filteredObjects[0]);
                }
                if (e.key==='Escape'){
                  setObjectDropdownOpen(false);
                  setObjectSearch(data.work_object||'');
                }
              }}
              style={input}
              placeholder="Начните вводить название или код"
            />
            {data.work_object && (
              <button type='button' onClick={()=> handleObjectSelect(null)} style={clearChipBtn}>Очистить</button>
            )}
            {objectDropdownOpen && (
              <div style={objectDropdownBox}>
                {filteredObjects.length === 0 ? (
                  <div style={objectOptionEmpty}>Ничего не найдено</div>
                ) : filteredObjects.map(obj => (
                  <div
                    key={obj.id || obj.code || obj.codeBase || obj.name}
                    style={objectOptionRow}
                    onMouseDown={e=> { e.preventDefault(); handleObjectSelect(obj); }}
                  >
                    <div style={{ fontWeight:600 }}>{obj.name || 'Без названия'}</div>
                    <div style={{ fontSize:12, color:'#6b7280' }}>{[obj.codeBase || obj.code || '—', obj.address || ''].filter(Boolean).join(' · ')}</div>
                  </div>
                ))}
              </div>
            )}
            {objectSelectionError && (
              <div style={fieldError}>{objectSelectionError}</div>
            )}
          </div>
          <div style={{ gridColumn:'span 2' }}>
            <label>Адрес работы (для договора)</label>
            <textarea rows={2} value={data.work_address||''} onChange={e=> onChange('work_address', e.target.value)} style={textareaInput} placeholder="Адрес для договора" />
          </div>
          {/* Убраны поля приема и увольнения как дублирующие период договора */}
          {isNew && (
            <div style={{ gridColumn:'span 4' }}>
              <div style={infoBox}>ID (логин) будет присвоен автоматически. Пароль = логин.</div>
            </div>
          )}
        </div>
      </section>

      {/* Должность и перевод */}
      <section style={cardStyle}>
        <h3 style={sectionTitle}>Должность</h3>
        <div style={grid4}>
          <div>
            <label>Текущая должность</label>
            <input value={data.position||''} readOnly style={roInput} />
          </div>
          {!isNew && (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              <div>
                <label>Перевести на</label>
                <select value={transferPositionId} onChange={e=> setTransferPositionId(e.target.value)} style={input}>
                  <option value=''>— выбрать —</option>
                  {positions.map(p=> <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label>Дата перевода</label>
                <input type='date' value={data._transfer_date||''} onChange={e=> onChange('_transfer_date', e.target.value)} style={input} />
              </div>
              <button type='button' onClick={doTransfer} disabled={!transferPositionId || !data._transfer_date} style={actionBtn}>Перевести</button>
            </div>
          )}
          {isNew && (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <div>
                <label>Должность (при создании)</label>
                <select value={positionId} onChange={e=> setPositionId(e.target.value)} style={input}>
                  <option value=''>— выбрать —</option>
                  {positions.map(p=> <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              {positionId && !autoSaveUnlocked && (
                <button onClick={handleInitialCreate} style={{ ...primaryBtn, alignSelf:'flex-start' }}>
                  Сохранить
                </button>
              )}
            </div>
          )}
        </div>
        {!isNew && (
          <div style={{ marginTop:16 }}>
            <strong>История переводов:</strong>
            {posHistory.length === 0 && <div style={hint}>нет записей</div>}
            {posHistory.length > 0 && (
              <table style={historyTable}>
                <thead>
                  <tr>
                    <th style={thCell}>Дата</th>
                    <th style={thCell}>Было</th>
                    <th style={thCell}>Стало</th>
                  </tr>
                </thead>
                <tbody>
                  {posHistory.map(h=> (
                    <tr key={h.id}>
                      <td style={tdCell}>{h.change_date||''}</td>
                      <td style={tdCell}>{h.old_position||'—'}</td>
                      <td style={tdCell}>{h.new_position}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>

      {/* Документы */}
      <section style={cardStyle}>
        <h3 style={sectionTitle}>Документы</h3>
        <div style={grid4}>
          <div>
            <label>Дата рождения</label>
            <input type='date' value={data.birth_date||''} onChange={e=> onChange('birth_date', e.target.value)} style={input} />
          </div>
          <div>
            <label>Серия паспорта</label>
            <input value={data.passport_series||''} onChange={e=> onChange('passport_series', e.target.value)} style={input} />
          </div>
          <div>
            <label>Номер паспорта</label>
            <input value={data.passport_number||''} onChange={e=> onChange('passport_number', e.target.value)} style={input} />
          </div>
          <div>
            <label>Дата выдачи паспорта</label>
            <input type='date' value={data.passport_issue_date||''} onChange={e=> onChange('passport_issue_date', e.target.value)} style={input} />
          </div>
          <div style={{ gridColumn:'span 2' }}>
            <label>Кем выдан паспорт</label>
            <input value={data.passport_issued_by||''} onChange={e=> onChange('passport_issued_by', e.target.value)} style={input} />
          </div>
          <div style={{ gridColumn:'span 4' }}>
            <label style={{ display:'inline-flex', alignItems:'center', gap:8, fontWeight:600 }}>
              <input
                type='checkbox'
                checked={!!data.has_residence_permit}
                onChange={e=> {
                  const checked = !!e.target.checked;
                  setData(prev => ({
                    ...prev,
                    has_residence_permit: checked,
                    residence_permit_series: checked ? (prev.residence_permit_series || '') : '',
                    residence_permit_number: checked ? (prev.residence_permit_number || '') : '',
                    residence_permit_issue_date: checked ? (prev.residence_permit_issue_date || '') : '',
                    residence_permit_issued_by: checked ? (prev.residence_permit_issued_by || '') : ''
                  }));
                }}
              />
              Добавить ВИД на жительство
            </label>
          </div>
          {data.has_residence_permit && (
            <>
              <div>
                <label>ВНЖ: серия</label>
                <input value={data.residence_permit_series||''} onChange={e=> onChange('residence_permit_series', e.target.value)} style={input} />
              </div>
              <div>
                <label>ВНЖ: номер</label>
                <input value={data.residence_permit_number||''} onChange={e=> onChange('residence_permit_number', e.target.value)} style={input} />
              </div>
              <div>
                <label>ВНЖ: дата выдачи</label>
                <input type='date' value={data.residence_permit_issue_date||''} onChange={e=> onChange('residence_permit_issue_date', e.target.value)} style={input} />
              </div>
              <div>
                <label>ВНЖ: кем выдан</label>
                <input value={data.residence_permit_issued_by||''} onChange={e=> onChange('residence_permit_issued_by', e.target.value)} style={input} />
              </div>
            </>
          )}
          {['Узбекистан','Таджикистан','Индия','Иран'].includes(data.citizenship) && (
            <>
              <div style={{ gridColumn:'span 4', marginTop:8 }}><strong>Патент</strong></div>
              <div>
                <label>Серия</label>
                <input value={data.patent_series||''} onChange={e=> onChange('patent_series', e.target.value)} style={input} />
              </div>
              <div>
                <label>Номер</label>
                <input value={data.patent_number||''} onChange={e=> onChange('patent_number', e.target.value)} style={input} />
              </div>
              <div>
                <label>Территория действия</label>
                <input value={data.patent_region||''} onChange={e=> onChange('patent_region', e.target.value)} style={input} />
              </div>
              <div>
                <label>Серия бланка</label>
                <input value={data.patent_form_series||''} onChange={e=> onChange('patent_form_series', e.target.value)} style={input} />
              </div>
              <div>
                <label>Номер бланка</label>
                <input value={data.patent_form_number||''} onChange={e=> onChange('patent_form_number', e.target.value)} style={input} />
              </div>
              <div>
                <label>Дата выдачи патента</label>
                <input type='date' value={data.patent_issue_date||''} onChange={e=> onChange('patent_issue_date', e.target.value)} style={input} />
              </div>
            </>
          )}
        </div>
      </section>

      {/* Комментарии */}
      {!isNew && (
        <section style={cardStyle}>
          <h3 style={sectionTitle}>Комментарии</h3>
          <div style={{ border:'1px solid #e5e7eb', borderRadius:8, padding:12, maxHeight:220, overflow:'auto', background:'#fafafa', marginBottom:12 }}>
            {commentList.length === 0 ? (
              <div style={{ color:'#6b7280' }}>Комментариев пока нет.</div>
            ) : (
              commentList.map((c, idx)=> (
                <div key={idx} style={{ padding:'6px 0', borderBottom: idx<commentList.length-1 ? '1px solid #e2e8f0' : 'none' }}>
                  <div style={{ fontSize:13, color:'#374151', marginBottom:4 }}>
                    <b>{c.name || c.uid || '—'}</b>{c.role ? `, ${c.role}` : ''} — {c.at ? new Date(c.at).toLocaleString('ru-RU') : ''}
                  </div>
                  <div style={{ whiteSpace:'pre-wrap' }}>{c.text}</div>
                </div>
              ))
            )}
          </div>
          {canComment ? (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              <textarea
                value={commentDraft}
                onChange={e=> setCommentDraft(e.target.value)}
                rows={3}
                placeholder="Новый комментарий..."
                style={{ width:'100%', padding:'8px 10px', border:'1px solid #c6c8d6', borderRadius:8 }}
              />
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={saveComment} disabled={commentSaving} style={{ padding:'6px 12px', background:'#e0f7e9', border:'1px solid #4caf50', borderRadius:6 }}>
                  {commentSaving? 'Сохраняю...' : 'Сохранить комментарий'}
                </button>
                {commentDraft && (
                  <button onClick={()=> setCommentDraft('')} disabled={commentSaving} style={{ padding:'6px 12px' }}>Очистить</button>
                )}
              </div>
            </div>
          ) : (
            <div style={{ color:'#6b7280', fontSize:13 }}>Комментирование доступно только HR/админу.</div>
          )}
        </section>
      )}

      {/* Договоры */}
      <section style={cardStyle}>
        <h3 style={sectionTitle}>Договоры</h3>
        <div style={grid4}>
          <div>
            <label>№ договора</label>
            <input
              value={data.contract_number||''}
              onChange={e=> onChange('contract_number', e.target.value)}
              readOnly={!allowManualContractFields}
              style={allowManualContractFields ? input : roInput}
              placeholder={allowManualContractFields ? 'Введите номер договора вручную' : ''}
            />
            {allowManualContractFields && (
              <div style={hint}>Доступно только суперюзеру: впишите номер из оформленного договора.</div>
            )}
          </div>
          <div>
            <label>Дата подписания</label>
            <input type='date' value={data.contract_date||''} readOnly style={roInput} />
          </div>
          <div>
            <label>Период с</label>
            <input type='date' value={data.contract_from||data.contract_date||''} readOnly style={roInput} />
          </div>
          <div>
            <label>Период по</label>
            <input type='date' value={data.contract_to||''} onChange={e=> onChange('contract_to', e.target.value)} style={input} />
          </div>
        </div>
        {!isNew && (
          <div style={{ marginTop:12 }}>
            <button type='button' onClick={openAssign} style={greenBtn}>Новый договор</button>
          </div>
        )}
        {!isNew && (
          <div style={{ marginTop:16 }}>
            <strong>История договоров:</strong>
            {contracts.length===0 && <div style={hint}>нет записей</div>}
            {contracts.length>0 && (
              <table style={historyTable}>
                <thead>
                  <tr>
                    <th style={thCell}>№</th>
                    <th style={thCell}>Дата подписания</th>
                    <th style={thCell}>Тип</th>
                    <th style={thCell}>Период с</th>
                    <th style={thCell}>Период по</th>
                    <th style={thCell}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map(c=> {
                    const typeRu = c.contract_type==='td'?'ТД':(c.contract_type==='gph'?'ГПХ':c.contract_type);
                    const hasDocument = !!c.document;
                    const isGenerating = contractGenerating === c.id;
                    return (
                      <tr key={c.id}>
                        <td style={tdCell}>{c.contract_number}</td>
                        <td style={tdCell}>{c.contract_date||''}</td>
                        <td style={tdCell}>{typeRu}</td>
                        <td style={tdCell}>{c.contract_from||c.contract_date||''}</td>
                        <td style={tdCell}>{c.contract_to||''}</td>
                        <td style={tdCell}>
                          <div style={contractActionsWrapper}>
                            <div style={contractDocButtons}>
                              {hasDocument ? (
                                <>
                                  <button style={contractDocBtn} onClick={()=> openContractDocument(c.document)}>Открыть договор</button>
                                  <button
                                    style={contractDocSecondaryBtn}
                                    disabled={isGenerating}
                                    onClick={()=> generateContractDoc(c.id)}
                                  >
                                    {isGenerating ? 'Переформируем…' : 'Переформировать'}
                                  </button>
                                </>
                              ) : (
                                <button
                                  style={contractDocBtn}
                                  disabled={isGenerating}
                                  onClick={()=> generateContractDoc(c.id)}
                                >
                                  {isGenerating ? 'Формируем…' : 'Сформировать договор'}
                                </button>
                              )}
                            </div>
                            <div style={contractDocButtons}>
                              <button style={{ ...miniBtn }} onClick={()=> setEditContractModal({ id:c.id, contract_to:c.contract_to||'' })}>Редактировать</button>
                              <button style={{ ...miniBtn, background:'#ffe5e5', border:'1px solid #f44336', color:'#c62828' }} onClick={()=> deleteContract(c.id)}>Удалить</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>

      {/* Оформление */}
      {!isNew && (
        <section style={cardStyle}>
          <h3 style={sectionTitle}>Оформление</h3>
          <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
            <div>
              <strong>Задачи при оформлении договора:</strong>
              {tasks.filter(t=> t.task_type==='start').length===0 && <div style={hint}>нет задач</div>}
              {tasks.filter(t=> t.task_type==='start').map(t=> (
                <div key={t.id} style={taskRowStyle}>
                  <div style={{ flex:1, display:'flex', flexDirection:'column' }}>
                    <a href={t.link} target='_blank' rel='noreferrer' style={{ textDecoration:'underline', color: t.completed? '#1e40af':'#dc2626', fontWeight: t.completed? 500:600 }}>{t.title}</a>
                    {!t.completed && <span style={notDoneBadge}>Не выполнено</span>}
                  </div>
                  {!t.completed && (
                    <button onClick={()=> openCompleteTask(t.id)} style={taskBtn}>Выполнено</button>
                  )}
                  {t.completed && (
                    <div style={doneBadge}>Выполнено {formatTaskInfo(t)}</div>
                  )}
                </div>
              ))}
            </div>
            <div>
              <strong>Задачи при завершении договора:</strong>
              {tasks.filter(t=> t.task_type==='end').length===0 && <div style={hint}>нет задач</div>}
                {tasks.filter(t=> t.task_type==='end').length===0 && data.contract_to && (
                  <button
                    type='button'
                    onClick={ensureEndTasks}
                    style={{ marginTop:6, padding:'4px 10px', fontSize:12, borderRadius:6, border:'1px solid #fca5a5', background:'#fff1f2', color:'#b91c1c', cursor:'pointer', fontWeight:600 }}
                  >
                    Создать задачи увольнения
                  </button>
                )}
                {tasks.filter(t=> t.task_type==='end').map(t=> (
                <div key={t.id} style={taskRowStyle}>
                  <div style={{ flex:1, display:'flex', flexDirection:'column' }}>
                    <a href={t.link} target='_blank' rel='noreferrer' style={{ textDecoration:'underline', color: t.completed? '#1e40af':'#dc2626', fontWeight: t.completed? 500:600 }}>{t.title}</a>
                    {!t.completed && <span style={notDoneBadge}>Не выполнено</span>}
                  </div>
                  {!t.completed && (
                    <button onClick={()=> openCompleteTask(t.id)} style={taskBtn}>Выполнено</button>
                  )}
                        {completeTaskModal && (
                          <div style={modalBackdrop}>
                            <div style={modalBox}>
                              <h3 style={{ marginTop:0 }}>Завершение задачи</h3>
                              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                                <div>
                                  <label>Дата выполнения</label>
                                  <input type='date' value={completeTaskModal.date} onChange={e=> setCompleteTaskModal(s=> ({ ...s, date:e.target.value }))} style={input} />
                                </div>
                              </div>
                              <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:18 }}>
                                <button onClick={closeCompleteTask} style={secondaryBtn}>Отмена</button>
                                <button onClick={confirmCompleteTask} style={greenBtn}>Подтвердить</button>
                              </div>
                            </div>
                          </div>
                        )}
                  {t.completed && (
                    <div style={doneBadge}>Выполнено {formatTaskInfo(t)}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <div style={autoSaveFooter}>
        <div style={{ ...autoSaveBadge, ...resolveAutoSaveTone(autoSaveState.status) }}>
          {autoSaveState.message}
        </div>
        <div style={{ flex:1 }} />
        <button onClick={()=> navigate('/hr/employees')} style={secondaryBtn}>К списку</button>
      </div>

      {documentsModalOpen && (
        <div style={modalBackdrop}>
          <div style={modalBoxWide}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <h3 style={{ margin:0 }}>{isArchivedDocsView? 'Архивные файлы' : 'Прикрепленные файлы'}</h3>
              <button onClick={()=> setDocumentsModalOpen(false)} style={secondaryBtn}>Закрыть</button>
            </div>
            {documentsBusy ? (
              <div style={{ padding:'20px 0' }}>Загрузка…</div>
            ) : visibleDocuments.length === 0 ? (
              <div style={emptyDocsBox}>Список пуст</div>
            ) : (
              <>
                <div style={docToolbar}>
                  <label style={docSelectAllLabel}>
                    <input
                      ref={masterDocSelectRef}
                      type='checkbox'
                      checked={visibleDocuments.length > 0 && allVisibleSelected}
                      onChange={(e)=> handleSelectAllDocuments(e.target.checked)}
                    />
                    Выделить все
                  </label>
                  <div style={docBulkActions}>
                    <button
                      style={{ ...secondaryBtn, padding:'8px 16px' }}
                      onClick={handleBulkDownload}
                      disabled={!hasSelectedDocuments}
                    >
                      Скачать выбранные{hasSelectedDocuments ? ` (${selectedDocuments.length})` : ''}
                    </button>
                    {hasSelectedDocuments && (
                      <button
                        style={{ ...secondaryBtn, padding:'8px 16px' }}
                        onClick={clearDocumentSelection}
                      >
                        Снять выделение
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={docTable}>
                    <thead>
                      <tr>
                        <th style={{ ...docTh, ...docSelectTh }}></th>
                        <th style={docTh}>Файл</th>
                        <th style={docTh}>Размер</th>
                        <th style={docTh}>Загрузил</th>
                        <th style={docTh}>Дата</th>
                        <th style={docTh}>Архив</th>
                        <th style={docTh}>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                        {visibleDocuments.map((doc, idx) => {
                        const docId = String(doc.id);
                        const displayName = decodeDocumentName(doc.name || doc.original_name || doc.filename || 'Без названия');
                        return (
                          <tr key={doc.id}>
                            <td style={docSelectCell}>
                              <input
                                type='checkbox'
                                checked={selectedDocIds.includes(docId)}
                                onChange={(e)=> handleDocumentSelect(docId, e.target.checked)}
                              />
                            </td>
                            <td style={docTd}>
                              <a
                                href={getDocumentUrl(doc)}
                                style={docLink}
                                title={displayName}
                                target='_blank'
                                rel='noreferrer'
                                onClick={(e)=> openDocumentPreview(e, doc, idx)}
                              >
                                {displayName}
                              </a>
                            </td>
                            <td style={docTd}>{formatBytes(doc.size)}</td>
                            <td style={docTd}>{doc.uploaded_name || doc.uploaded_by || '—'}</td>
                            <td style={docTd}>{formatDateTime(doc.createdAt)}</td>
                            <td style={docTd}>
                              <label style={archiveToggle}>
                                <input type='checkbox' checked={!!doc.archived} onChange={e=> toggleDocumentArchive(doc, e.target.checked)} />
                                Архив
                              </label>
                            </td>
                            <td style={docTd}>
                              <div style={docActions}>
                                <button onClick={()=> downloadDocumentFile(doc)} style={{ ...miniBtn, background:'#e0f2fe', border:'1px solid #7dd3fc', color:'#0369a1' }}>Скачать</button>
                                <button onClick={()=> deleteDocument(doc.id)} style={{ ...miniBtn, background:'#ffe4e6', border:'1px solid #f87171', color:'#b91c1c' }}>Удалить</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {currentPreviewDoc && (
        <div style={modalBackdrop} onClick={closeDocumentPreview}>
          <div ref={previewModalRef} style={{ ...modalBoxWide, width:'80%', maxWidth:1000, maxHeight:'90vh', display:'flex', flexDirection:'column', gap:12 }} onClick={(e)=> e.stopPropagation()}>
            <div style={{ display:'flex', alignItems:'center', gap:12, justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <button onClick={()=> stepDocumentPreview(-1)} style={miniBtn}>←</button>
                <button onClick={()=> stepDocumentPreview(1)} style={miniBtn}>→</button>
                <div style={{ fontWeight:600 }}>{decodeDocumentName(currentPreviewDoc.name || currentPreviewDoc.original_name || currentPreviewDoc.filename || 'Документ')}</div>
                <div style={{ fontSize:12, color:'#64748b' }}>{previewDocIndex+1} из {visibleDocuments.length}</div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                {isImageDoc(currentPreviewDoc) && (
                  <button
                    onClick={()=> setPreviewRotation((deg)=> (deg + 90) % 360)}
                    style={{ ...miniBtn, background:'#f8fafc', border:'1px solid #cbd5f5', color:'#0f172a' }}
                  >
                    Повернуть 90°
                  </button>
                )}
                {isImageDoc(currentPreviewDoc) && (
                  <>
                    <div style={{ ...miniBtn, cursor:'default', background:'#eef2ff', border:'1px solid #c7d2fe', color:'#3730a3' }}>
                      {Math.round(previewScale * 100)}%
                    </div>
                    <button
                      onClick={()=> setPreviewScale(prev => Math.min(5, Number((prev + 0.1).toFixed(2))))}
                      style={{ ...miniBtn, background:'#f8fafc', border:'1px solid #cbd5f5', color:'#0f172a' }}
                    >
                      Увеличить
                    </button>
                    <button
                      onClick={()=> setPreviewScale(prev => Math.max(0.3, Number((prev - 0.1).toFixed(2))))}
                      style={{ ...miniBtn, background:'#f8fafc', border:'1px solid #cbd5f5', color:'#0f172a' }}
                    >
                      Уменьшить
                    </button>
                    <button
                      onClick={()=> setPreviewScale(1)}
                      style={{ ...miniBtn, background:'#f8fafc', border:'1px solid #cbd5f5', color:'#0f172a' }}
                    >
                      100%
                    </button>
                  </>
                )}
                <button
                  onClick={togglePreviewFullscreen}
                  style={{ ...miniBtn, background:'#f8fafc', border:'1px solid #cbd5f5', color:'#0f172a' }}
                >
                  {isPreviewFullscreen ? 'Свернуть окно' : 'На весь экран'}
                </button>
                <a
                  href={getDocumentUrl(currentPreviewDoc)}
                  target='_blank'
                  rel='noreferrer'
                  style={{ ...miniBtn, textDecoration:'none' }}
                >Открыть в новой вкладке</a>
                <a
                  href={getDocumentUrl(currentPreviewDoc, { download:true })}
                  target='_blank'
                  rel='noreferrer'
                  style={{ ...miniBtn, textDecoration:'none' }}
                >Скачать</a>
                <button onClick={closeDocumentPreview} style={{ ...miniBtn, background:'#fee2e2', border:'1px solid #ef4444', color:'#991b1b' }}>Закрыть</button>
              </div>
            </div>
            <div onWheel={handlePreviewWheel} style={{ flex:1, minHeight:'60vh', background:'#111', borderRadius:12, overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center' }}>
              {isImageDoc(currentPreviewDoc) ? (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', maxWidth:'100%', maxHeight:'100%' }}>
                  <img
                    src={getDocumentUrl(currentPreviewDoc)}
                    alt='Просмотр документа'
                    style={{
                      maxWidth:'100%',
                      maxHeight:'100%',
                      objectFit:'contain',
                      background:'#111',
                      transform:`rotate(${previewRotation}deg) scale(${previewScale})`,
                      transition:'transform 0.15s ease-in-out'
                    }}
                  />
                </div>
              ) : isPdfDoc(currentPreviewDoc) ? (
                <iframe
                  src={getDocumentUrl(currentPreviewDoc)}
                  title='Просмотр документа'
                  style={{ flex:1, border:'none', background:'#111', width:'100%', height:'100%' }}
                />
              ) : (
                <div style={{ color:'#e2e8f0', textAlign:'center', padding:24 }}>
                  Предпросмотр недоступен для этого формата. Откройте в новой вкладке или скачайте файл.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showAssignModal && (
        <div style={modalBackdrop}>
          <div style={modalBox}>
            <h3 style={{ marginTop:0 }}>Новый договор</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:12 }}>
              <div>
                <label>Тип</label>
                <select value={assignState.contract_type} onChange={e=> updateAssign('contract_type', e.target.value)} style={input}>
                  <option value='gph'>ГПХ</option>
                  <option value='td'>ТД</option>
                </select>
              </div>
              <div>
                <label>Дата договора (подписание)</label>
                <input type='date' value={assignState.contract_date} onChange={e=> updateAssign('contract_date', e.target.value)} style={input} />
              </div>
              <div>
                <label>Период действия с</label>
                <input type='date' value={assignState.contract_from} onChange={e=> updateAssign('contract_from', e.target.value)} style={input} />
              </div>
              <div>
                <label>Период действия по</label>
                <input type='date' value={assignState.contract_to} onChange={e=> updateAssign('contract_to', e.target.value)} style={input} />
              </div>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:18 }}>
              <button onClick={closeAssign} style={secondaryBtn}>Отмена</button>
              <button onClick={confirmAssign} style={greenBtn}>Сохранить</button>
            </div>
          </div>
        </div>
      )}
      {duplicateModalOpen && isNew && duplicateMatches.length > 0 && (
        <div style={modalBackdrop}>
          <div style={modalBoxWide}>
            <h3 style={{ marginTop:0, marginBottom:10, color:'#b91c1c' }}>Сотрудник уже существует в базе</h3>
            <div style={{ marginBottom:14, color:'#374151' }}>
              Найдены совпадения более 90%. Данный сотрудник уже существует в базе. Перейдите в его карточку и создайте новый договор в ней.
            </div>
            <div style={duplicateListBox}>
              {duplicateMatches.map((match) => (
                <div key={match.id} style={duplicateRow}>
                  <div>
                    <div style={{ fontWeight:700 }}>{match.full_name || '—'}</div>
                    <div style={{ fontSize:12, color:'#6b7280' }}>
                      Совпадение: {Math.round(Number(match.similarity || 0))}%{match.position ? ` · ${match.position}` : ''}{match.user_id ? ` · ID ${match.user_id}` : ''}
                    </div>
                  </div>
                  <a
                    href={`/hr/employees/${encodeURIComponent(match.id)}`}
                    onClick={(e)=> { e.preventDefault(); setDuplicateModalOpen(false); navigate(`/hr/employees/${encodeURIComponent(match.id)}`); }}
                    style={duplicateLinkBtn}
                  >
                    Открыть карточку
                  </a>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}>
              <button onClick={()=> setDuplicateModalOpen(false)} style={secondaryBtn}>Закрыть</button>
            </div>
          </div>
        </div>
      )}
      {/* Модальное окно завершения задачи — вне map, чтобы не дублировалось */}
      {completeTaskModal && (
        <div style={modalBackdrop}>
          <div style={modalBox}>
            <h3 style={{ marginTop:0 }}>Завершение задачи</h3>
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div>
                <label>Дата выполнения</label>
                <input type='date' value={completeTaskModal.date} onChange={e=> setCompleteTaskModal(s=> ({ ...s, date:e.target.value }))} style={input} />
              </div>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:18 }}>
              <button onClick={closeCompleteTask} style={secondaryBtn}>Отмена</button>
              <button onClick={confirmCompleteTask} style={greenBtn}>Подтвердить</button>
            </div>
          </div>
        </div>
      )}
      {editContractModal && (
        <div style={modalBackdrop}>
          <div style={modalBox}>
            <h3 style={{ marginTop:0 }}>Правка договора</h3>
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div>
                <label>Новый срок (дата "до")</label>
                <input type='date' value={editContractModal.contract_to} onChange={e=> setEditContractModal(s=> ({ ...s, contract_to:e.target.value }))} style={input} />
              </div>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:18 }}>
              <button onClick={()=> setEditContractModal(null)} style={secondaryBtn}>Отмена</button>
              <button onClick={saveContractEdit} style={primaryBtn}>Сохранить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Стили вынесены в константы для наглядности
const cardStyle = { background:'#f9fafb', padding:24, border:'1px solid #e5e7eb', borderRadius:14, boxShadow:'0 4px 12px rgba(0,0,0,0.05)', marginTop:24 };
const sectionTitle = { marginTop:0, marginBottom:16, fontSize:18 };
const grid4 = { display:'grid', gridTemplateColumns:'repeat(4, minmax(0,1fr))', gap:16 };
const fioWrapper = { gridColumn:'1 / -1', display:'flex', flexDirection:'column', gap:8 };
const fioGrid = { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:12 };
const input = { width:'100%', padding:10, border:'1px solid #d1d5db', borderRadius:8, background:'#fff' };
const textareaInput = { ...input, resize:'vertical', minHeight:68, lineHeight:1.35 };
const roInput = { ...input, background:'#eef2f7', fontWeight:600 };
const hint = { fontSize:12, color:'#6b7280', marginTop:4 };
const infoBox = { fontSize:13, color:'#374151', background:'#eef2f7', padding:'12px 14px', borderRadius:8 };
const fieldError = { color:'#b91c1c', fontSize:12, marginTop:6 };
const primaryBtn = { padding:'12px 20px', background:'#2563eb', color:'#fff', border:'1px solid #1d4ed8', borderRadius:8, fontWeight:600, cursor:'pointer' };
const secondaryBtn = { padding:'12px 20px', background:'#f3f4f6', border:'1px solid #d1d5db', borderRadius:8, cursor:'pointer' };
const greenBtn = { padding:'10px 16px', background:'#16a34a', color:'#fff', border:'1px solid #15803d', borderRadius:8, cursor:'pointer' };
const actionBtn = { marginTop:8, padding:'8px 14px', background:'#fff3e0', border:'1px solid #fb8c00', borderRadius:8, cursor:'pointer' };
const autoSaveFooter = { display:'flex', gap:12, marginTop:24, alignItems:'center', flexWrap:'wrap' };
const autoSaveBadge = { padding:'10px 16px', borderRadius:999, fontSize:13, fontWeight:600, border:'1px solid transparent' };
const historyTable = { width:'100%', borderCollapse:'collapse', marginTop:8 };
const modalBackdrop = { position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 };
const modalBox = { background:'#fff', padding:24, borderRadius:14, width:460, boxShadow:'0 10px 30px rgba(0,0,0,0.25)' };
const thCell = { textAlign:'left', padding:'6px 10px', border:'1px solid #dfe3e9', background:'#f5f7fa', fontWeight:600, fontSize:13 };
const tdCell = { padding:'6px 10px', border:'1px solid #ebeef2', fontSize:13, background:'#fff' };
const miniBtn = { padding:'4px 8px', background:'#e0f2ff', border:'1px solid #2196f3', borderRadius:6, fontSize:12, cursor:'pointer' };
const contractActionsWrapper = { display:'flex', flexDirection:'column', gap:8 };
const contractDocButtons = { display:'flex', flexWrap:'wrap', gap:8 };
const contractDocBtn = { ...miniBtn, background:'#e0f2fe', border:'1px solid #93c5fd', color:'#1d4ed8' };
const contractDocSecondaryBtn = { ...miniBtn, background:'#ede9fe', border:'1px solid #c4b5fd', color:'#5b21b6' };
const taskRowStyle = { display:'flex', alignItems:'center', gap:12, padding:'10px 12px', border:'1px solid #e2e8f0', borderRadius:10, background:'#fff', marginTop:8 };
const taskBtn = { padding:'6px 10px', background:'#fef3c7', border:'1px solid #f59e0b', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600, color:'#92400e' };
const doneBadge = { fontSize:12, fontWeight:600, color:'#065f46', background:'#d1fae5', padding:'4px 8px', borderRadius:8 };
const notDoneBadge = { fontSize:12, fontWeight:600, color:'#dc2626', background:'#fee2e2', padding:'2px 6px', borderRadius:6, alignSelf:'flex-start', marginTop:4 };
const modalBoxWide = { ...modalBox, width:720, maxHeight:'80vh', overflow:'hidden' };
const docTable = { width:'100%', borderCollapse:'collapse', marginTop:12 };
const docTh = { textAlign:'left', padding:'8px 10px', borderBottom:'1px solid #e2e8f0', fontSize:12, textTransform:'uppercase', color:'#475569' };
const docTd = { padding:'8px 10px', borderBottom:'1px solid #f1f5f9', fontSize:13, verticalAlign:'top' };
const docSelectTh = { width:40, textAlign:'center', padding:'8px 6px' };
const docSelectCell = { ...docTd, textAlign:'center', width:40, padding:'8px 6px' };
const docToolbar = { display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap', marginTop:12 };
const docSelectAllLabel = { display:'flex', alignItems:'center', gap:8, fontSize:13 };
const docBulkActions = { display:'flex', gap:8, flexWrap:'wrap' };
const docActions = { display:'flex', gap:8, flexWrap:'wrap' };
const archiveToggle = { display:'flex', alignItems:'center', gap:6, fontSize:12 };
const emptyDocsBox = { padding:'24px 12px', textAlign:'center', color:'#6b7280', border:'1px dashed #cbd5f5', borderRadius:12, marginTop:12 };
const docLink = { color:'#2563eb', textDecoration:'underline', fontWeight:600 };
const duplicateListBox = { display:'flex', flexDirection:'column', gap:8, maxHeight:320, overflowY:'auto', border:'1px solid #fee2e2', borderRadius:10, padding:10, background:'#fff7f7' };
const duplicateRow = { display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, border:'1px solid #fecaca', borderRadius:8, padding:'10px 12px', background:'#fff' };
const duplicateLinkBtn = { padding:'6px 12px', borderRadius:8, border:'1px solid #93c5fd', background:'#eff6ff', color:'#1d4ed8', textDecoration:'none', fontWeight:600, whiteSpace:'nowrap' };
const objectDropdownBox = { position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:'#fff', border:'1px solid #d1d5db', borderRadius:10, boxShadow:'0 10px 25px rgba(15,23,42,0.15)', maxHeight:220, overflowY:'auto', zIndex:30 };
const objectOptionRow = { padding:'8px 10px', borderBottom:'1px solid #eef2f7', cursor:'pointer' };
const objectOptionEmpty = { padding:'10px 12px', color:'#6b7280', fontSize:13 };
const clearChipBtn = { position:'absolute', top:32, right:8, padding:'2px 8px', fontSize:12, border:'none', background:'transparent', color:'#2563eb', cursor:'pointer' };
const photoActionsWrapper = { display:'flex', gap:18, alignItems:'center', marginBottom:16 };
const photoPreviewBox = { width:140, height:140, borderRadius:'50%', overflow:'hidden', border:'2px solid #e5e7eb', display:'flex', alignItems:'center', justifyContent:'center', background:'#f8fafc' };
const photoImg = { width:'100%', height:'100%', objectFit:'cover' };
const photoPlaceholder = { fontSize:13, color:'#94a3b8' };
const citizenshipOptions = ['РФ','Беларусь','Казахстан','Узбекистан','Таджикистан','Кыргызстан','Армения','Грузия','Украина','Молдова','Индия','Иран','Китай','Другое'];

const autoSaveToneMap = {
  idle: { color:'#475569', background:'#f8fafc', border:'1px solid #cbd5f5' },
  pending: { color:'#92400e', background:'#fff7ed', border:'1px solid #fdba74' },
  saving: { color:'#0f172a', background:'#e0f2fe', border:'1px solid #7dd3fc' },
  saved: { color:'#166534', background:'#dcfce7', border:'1px solid #86efac' },
  blocked: { color:'#b45309', background:'#fffbeb', border:'1px solid #fde68a' },
  error: { color:'#b91c1c', background:'#fee2e2', border:'1px solid #fecaca' }
};

function resolveAutoSaveTone(status){
  return autoSaveToneMap[status] || autoSaveToneMap.idle;
}

function composeFullName(lastName, firstName, middleName){
  return [lastName, firstName, middleName]
    .map(part => (part || '').trim())
    .filter(Boolean)
    .join(' ')
    .trim();
}

function splitFullNameToParts(value){
  const source = (value || '').trim();
  if (!source) return { last_name:'', first_name:'', middle_name:'' };
  const parts = source.split(/\s+/);
  const last_name = parts.shift() || '';
  const first_name = parts.shift() || '';
  const middle_name = parts.length ? parts.join(' ') : '';
  return { last_name, first_name, middle_name };
}

function normalizeEmployeeNameState(state){
  const next = { ...state };
  if ((!next.last_name || !next.first_name) && next.full_name){
    const parsed = splitFullNameToParts(next.full_name);
    if (!next.last_name) next.last_name = parsed.last_name;
    if (!next.first_name) next.first_name = parsed.first_name;
    if (!next.middle_name) next.middle_name = parsed.middle_name;
  }
  next.last_name = (next.last_name || '').trim();
  next.first_name = (next.first_name || '').trim();
  next.middle_name = (next.middle_name || '').trim();
  const composed = composeFullName(next.last_name, next.first_name, next.middle_name);
  next.full_name = composed || (next.full_name || '');
  return next;
}

function mergeEmployeeState(prev, patch){
  const merged = { ...prev, ...(patch || {}) };
  return normalizeEmployeeNameState(merged);
}

function buildComparableState(source, positionDraft){
  const fields = [
    'last_name','first_name','middle_name','position','passport_series','passport_number','passport_issue_date','passport_issued_by','birth_date',
    'citizenship','registration','registration_to','patent_series','patent_number','patent_region','patent_form_series','patent_form_number',
    'patent_issue_date','has_residence_permit','residence_permit_series','residence_permit_number','residence_permit_issue_date','residence_permit_issued_by','contract_type','contract_number','phone','email','work_object','work_address','contract_to'
  ];
  const base = {};
  const safeSource = source || {};
  fields.forEach(field => {
    const value = safeSource[field];
    base[field] = value === undefined || value === null ? '' : value;
  });
  base.__positionDraft = positionDraft ? String(positionDraft) : '';
  return base;
}

function describeAutoSaveError(error){
  const raw = error && error.message ? String(error.message) : String(error || '');
  if (/duplicate_employee/i.test(raw)) return 'Сотрудник уже существует в базе. Перейдите в карточку и добавьте новый договор.';
  if (/invalid_email/i.test(raw)) return 'Неверный формат e-mail';
  if (/blocked_position/i.test(raw)) return 'Выбранная должность недоступна';
  if (/forbidden/i.test(raw)) return 'Недостаточно прав для сохранения';
  if (raw === 'server_error') return 'Сервер временно недоступен. Попробуйте позже.';
  return raw ? `Не удалось сохранить: ${raw}` : 'Не удалось сохранить изменения';
}

function formatObjectLabel(obj){
  if (!obj) return '';
  const code = obj.codeBase || obj.code || obj.object_code;
  const name = obj.name || obj.title || obj.label || obj.id || 'Без названия';
  return code ? `${code} · ${name}` : name;
}

function findObjectMatch(searchValue, list){
  const q = String(searchValue||'').trim().toLowerCase();
  if (!q) return null;
  const exact = list.find(obj => {
    const name = String(obj.name||'').trim().toLowerCase();
    const code = String(obj.codeBase||obj.code||obj.object_code||'').trim().toLowerCase();
    const label = formatObjectLabel(obj).toLowerCase();
    return (name && name===q) || (code && code===q) || (label && label===q);
  });
  if (exact) return exact;
  return list.find(obj => {
    const name = String(obj.name||'').toLowerCase();
    const code = String(obj.codeBase||obj.code||obj.object_code||'').toLowerCase();
    return (name && name.includes(q)) || (code && code.includes(q));
  }) || null;
}
